import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  doc,
  getDoc
} from "firebase/firestore";
import { db } from "./firebase";
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "./firebase";
import { ClickableDancerName, ClickableStudioName } from "./utils/ClickableNames";
import DancerThumbnail from "./DancerThumbnail";
import SponsorshipDisplay from "./components/SponsorshipDisplay";
import { useSponsorship } from "./hooks/useSponsorship";
import { useAdmin } from "./hooks/useAdmin";
import "./DancerThumbnail.css";
import { getLatestTransferStudios, getProfileStudio } from "./utils/studioLookup";
import { getDancerCurrentAgeCategory, shouldResultCountForRankings, getDancerAgeCategoryForYear, wasInCorrectCategoryAtCompetition } from "./utils/ageCategoryUtils";

// Preload profile pictures in parallel for instant display
const preloadProfilePictures = async (dancerNames) => {
  // Fetch all profile pictures in parallel (not sequential)
  const promises = dancerNames.map(async (dancerName) => {
    try {
      // Check dancerProfiles first
      const profileRef = doc(db, 'dancerProfiles', dancerName);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        const profileData = profileSnap.data();
        if (profileData.profilePicture) {
          if (profileData.profilePicture.startsWith('https://')) {
            return profileData.profilePicture;
          } else {
            const pictureRef = ref(storage, profileData.profilePicture);
            return await getDownloadURL(pictureRef);
          }
        }
      }
      
      // Fallback to dancers collection
      const dancerRef = doc(db, 'dancers', dancerName);
      const dancerSnap = await getDoc(dancerRef);
      
      if (dancerSnap.exists()) {
        const dancerData = dancerSnap.data();
        if (dancerData.profilePicture) {
          const pictureRef = ref(storage, dancerData.profilePicture);
          return await getDownloadURL(pictureRef);
        }
      }
    } catch (error) {
      // Silently fail - DancerThumbnail will handle showing placeholder
      console.log(`Could not preload picture for ${dancerName}:`, error);
    }
    return null;
  });
  
  // Wait for all to complete in parallel
  await Promise.all(promises);
};

export default function Leaderboard({ discipline, category, dance }) {
  const [loading, setLoading] = useState(true);
  const [sortedDancers, setSortedDancers] = useState([]);
  const [error, setError] = useState(null);
  const { isSponsored } = useSponsorship();
  const { isAdmin } = useAdmin();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const oneYearAgo = new Date();
        oneYearAgo.setDate(oneYearAgo.getDate() - 365);

        const filters = [
          where("discipline", "==", discipline),
          where("category", "==", category),
          ...(discipline === "Latin" ? [where("dance", "==", dance)] : []),
          where("timestamp", ">=", Timestamp.fromDate(oneYearAgo)),
          orderBy("timestamp", "desc")
        ];

        const q = query(collection(db, "competitions"), ...filters);
        const snapshot = await getDocs(q);
        const dancerData = {};

        if (snapshot.empty) {
          console.warn("⚠️ No competition documents matched query.");
        }

        // First pass: collect all dancer names and competition years they participated in
        const allDancerNames = new Set();
        const dancerCompetitionYears = {}; // { dancerName: Set of years }
        snapshot.forEach((doc) => {
          const comp = doc.data();
          const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
          const compYear = compDate.getFullYear();
          
          if (Array.isArray(comp.results)) {
            comp.results.forEach((r) => {
              if (r.dancerName && !r.excluded) {
                allDancerNames.add(r.dancerName);
                if (!dancerCompetitionYears[r.dancerName]) {
                  dancerCompetitionYears[r.dancerName] = new Set();
                }
                dancerCompetitionYears[r.dancerName].add(compYear);
              }
            });
          }
        });

        // Get age categories for all dancers (current + for each competition year)
        const dancerAgeCategories = {};
        const dancerAgeCategoriesByYear = {}; // { dancerName: { year: category } }
        await Promise.all(
          Array.from(allDancerNames).map(async (name) => {
            dancerAgeCategories[name] = await getDancerCurrentAgeCategory(name);
            dancerAgeCategoriesByYear[name] = {};
            
            // Get age category for each year they competed
            const years = dancerCompetitionYears[name] || new Set();
            await Promise.all(
              Array.from(years).map(async (year) => {
                dancerAgeCategoriesByYear[name][year] = await getDancerAgeCategoryForYear(name, year);
              })
            );
          })
        );

        // Second pass: process results with age category filtering
        snapshot.forEach((doc) => {
          const comp = doc.data();
          const multiplier = comp.multiplier || 1;
          const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
          const compYear = compDate.getFullYear();

          if (!Array.isArray(comp.results)) {
            console.warn(`⚠️ Competition ${doc.id} missing valid results array.`);
            return;
          }

          comp.results.forEach((r) => {
            if (!r.dancerName) return;
            
            // Skip excluded results
            if (r.excluded === true) {
              console.log(`⏭️ Skipping excluded result for ${r.dancerName} in competition ${doc.id}`);
              return;
            }
            
            const name = r.dancerName;

            // Check if this result should count for rankings based on current age category
            if (!shouldResultCountForRankings(category, dancerAgeCategories[name])) {
              console.log(`⏭️ Skipping ${name}'s result from category ${category} (current age category: ${dancerAgeCategories[name]})`);
              return;
            }

            // Check if dancer was in the CORRECT category at the time of competition
            // (filter out results where dancer competed in wrong age category)
            const ageCategoryAtCompetition = dancerAgeCategoriesByYear[name]?.[compYear];
            if (!wasInCorrectCategoryAtCompetition(category, ageCategoryAtCompetition)) {
              console.log(`⏭️ Skipping ${name}'s result - competed in ${category} but should have been in ${ageCategoryAtCompetition} at time of competition (${compYear})`);
              return;
            }
            
            const score = Math.round((r.points || 0) * multiplier);
            if (!dancerData[name]) {
              dancerData[name] = { studio: "-", studioTime: -Infinity, scores: [], total: 0, competitions: new Set() };
            }
            // Studio = from the most recent result that actually has one (the first result may be blank)
            if (r.studio && compDate.getTime() >= dancerData[name].studioTime) {
              dancerData[name].studio = r.studio;
              dancerData[name].studioTime = compDate.getTime();
            }
            dancerData[name].scores.push(score);
            dancerData[name].competitions.add(doc.id); // Track unique competitions
          });
        });

        Object.values(dancerData).forEach((dancer) => {
          const top6 = dancer.scores.sort((a, b) => b - a).slice(0, 6);
          dancer.total = top6.reduce((sum, v) => sum + v, 0);
          dancer.competitionCount = dancer.competitions.size; // Count unique competitions
        });

        const sorted = Object.entries(dancerData)
          .map(([name, data]) => ({ name, studio: data.studio, total: data.total, competitionCount: data.competitionCount }))
          .sort((a, b) => b.total - a.total);
        // Assign places with tie handling
        let lastPoints = null;
        let lastPlace = 0;
        let tieCount = 0;
        for (let i = 0; i < sorted.length; i++) {
          const d = sorted[i];
          if (lastPoints === d.total) {
            d.place = lastPlace;
            tieCount++;
          } else {
            d.place = lastPlace + tieCount + 1;
            lastPlace = d.place;
            lastPoints = d.total;
            tieCount = 0;
          }
        }

        // Fill in studios: an active studio transfer wins (same as Eternal Cup), and dancers
        // whose results carry no studio fall back to the studio assigned on their profile.
        const transferStudios = await getLatestTransferStudios();
        await Promise.all(sorted.map(async (d) => {
          const transferred = transferStudios.get(d.name);
          if (transferred) {
            d.studio = transferred;
          } else if (!d.studio || d.studio === "-") {
            d.studio = (await getProfileStudio(d.name, discipline)) || "-";
          }
        }));

        setSortedDancers(sorted);
        
        // Preload profile pictures in parallel for faster display
        preloadProfilePictures(sorted.map(d => d.name));
      } catch (err) {
        console.error("❌ Leaderboard fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [discipline, category, dance]);

  if (loading) {
    return (
      <div>
        <h3>Star Ranking - Last 365 Days</h3>
        <div className="leaderboard-skeleton">
          {[...Array(8)].map((_, index) => (
            <div key={index} className="skeleton-row">
              <div className="skeleton skeleton-circle"></div>
              <div style={{ flex: 1 }}>
                <div className="skeleton skeleton-text large" style={{ width: '60%' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '40%' }}></div>
              </div>
              <div className="skeleton skeleton-text" style={{ width: '80px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '60px' }}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  if (error) return <p>Error: {error}</p>;

  return (
    <div>
      {/* Rankings Table */}
      {sortedDancers.length > 0 && (
        <div>
          <h3>Star Ranking - Last 365 Days</h3>
          <p style={{ fontSize: "0.9em", color: "#666", marginBottom: "1rem" }}>
            Showing {sortedDancers.length} dancers with competition results from the last year
          </p>

          {/* Desktop Header */}
          <div className="leaderboard-header">
            <div style={{ textAlign: "center" }}>Place</div>
            <div></div> {/* Space for profile picture */}
            <div>Dancer</div>
            <div style={{ textAlign: "center" }}>Points</div>
            {isAdmin && <div style={{ textAlign: "center" }}>Competitions</div>}
          </div>

          {/* Responsive Dancer Rows */}
          {sortedDancers.map((d) => {
            // Define border colors for top 3
            let borderColor = "#e0e0e0";
            if (d.place === 1) borderColor = "#ffd700"; // Gold
            else if (d.place === 2) borderColor = "#c0c0c0"; // Silver
            else if (d.place === 3) borderColor = "#cd7f32"; // Bronze

            return (
              <div 
                key={d.name}
                className="leaderboard-row"
                style={{
                  backgroundColor: d.place <= 3 ? "#fff3e0" : "white",
                  borderColor: borderColor
                }}
              >
                {/* Place and Points */}
                <div className="leaderboard-place">
                  <div className="place-badge">
                    {d.place === 1 && "🥇"}
                    {d.place === 2 && "🥈"}
                    {d.place === 3 && "🥉"}
                    {d.place > 3 && `#${d.place}`}
                  </div>
                  <div className="points-under-place">
                    <span className="mobile-label">Points:</span>
                    <span className="points-value">{d.total}</span>
                  </div>
                </div>

                {/* Profile Picture */}
                <div className="leaderboard-photo">
                  <DancerThumbnail 
                    dancerName={d.name} 
                    size={50} 
                    showPlaceholder={true}
                  />
                </div>

                {/* Dancer Info - More space for names */}
                <div className="leaderboard-dancer-info">
                  <div className="dancer-name">
                    <ClickableDancerName name={d.name}>
                      {d.name}
                    </ClickableDancerName>
                    <SponsorshipDisplay 
                      dancerName={d.name}
                      isSponsored={isSponsored(d.name)}
                      size="small"
                      layout="horizontal"
                      position="inline-right"
                    />
                  </div>
                  <div className="dancer-studio">
                    <ClickableStudioName name={d.studio} dancerName={d.name} />
                  </div>
                </div>

                {/* Points - Desktop Only (Mobile shows under place) */}
                <div className="leaderboard-points desktop-only">
                  <span className="points-value">{d.total}</span>
                </div>

                {/* Competitions - Admin Only */}
                {isAdmin && (
                  <div className="leaderboard-competitions">
                    <span className="mobile-label">Competitions:</span>
                    <span className="competitions-value">{d.competitionCount}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* No Results State */}
      {sortedDancers.length === 0 && (
        <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
          <p>No competition results found for the selected criteria in the last 365 days.</p>
        </div>
      )}
    </div>
  );
}
